<!-- Test Mode Top Bar -->
@if (isTestMode()) {
  <div
    class="fixed top-0 left-0 right-0 h-11 z-[70] bg-amber-500 flex items-center justify-center px-4"
  >
    <div class="flex items-center gap-2">
      <lucide-icon
        [img]="icons.FlaskConical"
        class="w-4 h-4 text-white"
      ></lucide-icon>
      <span class="text-sm font-semibold text-white">
        You're previewing this as a student
      </span>
    </div>
    <button
      type="button"
      (click)="onLeaveToDashboard()"
      class="absolute right-3 w-7 h-7 flex items-center justify-center rounded-full text-white/80 hover:text-white hover:bg-white/15 transition cursor-pointer"
      aria-label="Exit test mode"
    >
      <lucide-icon [img]="icons.X" class="w-4 h-4"></lucide-icon>
    </button>
  </div>
}

<div
  class="min-h-screen bg-[#FFFDF5] text-gray-900 font-sans selection:bg-green-500/20 pb-safe"
  [class.pt-11]="isTestMode()"
>
  <!-- Header with Timer (only during questions) -->
  @if ((currentStep$ | async) === StudentAssignmentStep.QUESTIONS) {
    <header
      class="sticky z-50 bg-[#FFFDF5]/95 backdrop-blur-sm border-b border-gray-100"
      [ngClass]="isTestMode() ? 'top-11' : 'top-0'"
    >
      <div
        class="max-w-lg mx-auto px-4 py-3 flex items-center justify-between"
      >
        <!-- Close / Dashboard Button -->
        <button
          type="button"
          (click)="onLeaveClick()"
          class="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition cursor-pointer"
          aria-label="Go to dashboard"
        >
          <lucide-icon [img]="icons.X" class="w-5 h-5"></lucide-icon>
        </button>

        <!-- Question Progress -->
        <div class="text-sm text-gray-500">
          Question {{ (currentQuestionIndex$ | async)! + 1 }} of
          {{ getTotalQuestions() }}
        </div>

        <!-- Timer -->
        @if (expiresAt$ | async; as expiresAt) {
          <app-quiz-timer
            [expiresAt]="expiresAt"
            (expired)="onTimeExpired()"
          ></app-quiz-timer>
        } @else {
          <!-- Spacer to keep progress centered when no timer -->
          <div class="w-8"></div>
        }
      </div>
    </header>
  }

  <main class="pt-1 pb-8 px-4 md:px-8">
    <!-- Loading State -->
    @if (isLoading$ | async) {
      <div class="flex items-center justify-center py-20">
        <div
          class="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin"
          [style.border-color]="themeColor"
          [style.border-top-color]="'transparent'"
        ></div>
      </div>
    } @else {
      @switch (currentStep$ | async) {
        <!-- Preview Step -->
        @case (StudentAssignmentStep.PREVIEW) {
          <app-assignment-preview
            [preview]="preview$ | async"
            [isLoading]="(isLoading$ | async) ?? false"
            (start)="onStartAssignment()"
          ></app-assignment-preview>
        }

        <!-- Questions Step -->
        @case (StudentAssignmentStep.QUESTIONS) {
          @if (currentQuestion$ | async; as question) {
            @if (localAnswers$ | async; as localAnswers) {
              <app-question-view
                [question]="question"
                [currentIndex]="(currentQuestionIndex$ | async) ?? 0"
                [totalQuestions]="getTotalQuestions()"
                [answer]="getLocalAnswer(localAnswers, question.id)"
                [isSubmitting]="(isSubmitting$ | async) ?? false"
                (answerChange)="onAnswerChange($event)"
                (next)="onNextQuestion()"
                (previous)="onPreviousQuestion()"
                (submit)="onSubmit()"
              ></app-question-view>
            }
          }
        }

        <!-- Result Step -->
        @case (StudentAssignmentStep.RESULT) {
          <app-quiz-result
            [result]="result$ | async"
            [isGrading]="(isGrading$ | async) ?? false"
            [gradingProgressStep]="(gradingProgressStep$ | async) ?? 0"
            [submittedAt]="(result$ | async)?.submittedAt ?? null"
            [isTestMode]="isTestMode()"
            (continue)="onContinue()"
            (retest)="onRetestFromResult()"
          ></app-quiz-result>
        }

        <!-- Error Step -->
        @case (StudentAssignmentStep.ERROR) {
          <div
            class="max-w-md mx-auto text-center py-12 animate-in fade-in slide-in-from-bottom-4 duration-500"
          >
            <div
              class="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center bg-red-100"
            >
              <lucide-icon
                [img]="icons.AlertTriangle"
                class="w-10 h-10 text-red-500"
              ></lucide-icon>
            </div>

            @if (isTeacherError) {
              <h1 class="text-2xl font-bold text-gray-900 mb-2">
                Access Denied
              </h1>
              <p class="text-gray-500 mb-8">
                Teachers cannot take assignments. Please use a student account
                to complete this assignment.
              </p>
            } @else {
              <h1 class="text-2xl font-bold text-gray-900 mb-2">
                @switch (errorType$ | async) {
                  @case (StudentAssignmentErrorType.NOT_FOUND) {
                    Assignment Not Found
                  }
                  @case (StudentAssignmentErrorType.EXPIRED) {
                    Assignment Expired
                  }
                  @case (StudentAssignmentErrorType.ALREADY_SUBMITTED) {
                    Already Submitted
                  }
                  @default {
                    Something Went Wrong
                  }
                }
              </h1>
              <p class="text-gray-500 mb-8">{{ error$ | async }}</p>
            }

            <button
              type="button"
              (click)="onContinue()"
              class="px-8 py-3 rounded-xl font-semibold text-white transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
              [style.background-color]="themeColor"
            >
              {{ isTestMode() ? 'Back to Assignment' : 'Go to Dashboard' }}
            </button>
          </div>
        }
      }
    }
  </main>
</div>

<!-- Retest Confirmation Modal (Teacher test mode) -->
@if (isRetestModalOpen()) {
  <div class="fixed inset-0 z-[100] flex items-center justify-center">
    <!-- Backdrop -->
    <div
      class="absolute inset-0 bg-black/50 leave-modal-fade-in"
      (click)="onRetestCancel()"
    ></div>

    <!-- Modal -->
    <div
      class="relative z-10 w-full max-w-sm mx-4 bg-white rounded-2xl shadow-2xl leave-modal-scale-in overflow-hidden"
    >
      <!-- Header -->
      <div class="px-6 pt-6 pb-4">
        <div
          class="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4"
        >
          <lucide-icon
            [img]="icons.FlaskConical"
            class="w-6 h-6 text-amber-600"
          ></lucide-icon>
        </div>
        <h2 class="text-lg font-bold text-gray-900 text-center mb-2">
          Retest Assignment?
        </h2>
        <p class="text-sm text-gray-500 text-center">
          You've already tested this assignment. Starting a new test will clear
          your previous test submission and start fresh.
        </p>
      </div>

      <!-- Actions -->
      <div class="px-6 pb-6 flex flex-col gap-3">
        <button
          type="button"
          (click)="onRetestConfirm()"
          class="w-full py-3 px-4 rounded-xl font-semibold text-white text-sm transition-all duration-200 hover:opacity-90 active:scale-[0.98] cursor-pointer"
          [style.background-color]="themeColor"
        >
          Start New Test
        </button>
        <button
          type="button"
          (click)="onRetestCancel()"
          class="w-full py-3 px-4 rounded-xl font-semibold text-gray-700 text-sm bg-gray-100 hover:bg-gray-200 transition-all duration-200 active:scale-[0.98] cursor-pointer flex items-center justify-center gap-2"
        >
          <span>Back to Assignment</span>
          <lucide-icon
            [img]="icons.ArrowRight"
            class="w-4 h-4"
          ></lucide-icon>
        </button>
      </div>
    </div>
  </div>
}

<!-- Leave Confirmation Modal -->
@if (isLeaveModalOpen()) {
  <div class="fixed inset-0 z-[100] flex items-center justify-center">
    <!-- Backdrop -->
    <div
      class="absolute inset-0 bg-black/50 leave-modal-fade-in"
      (click)="onStay()"
    ></div>

    <!-- Modal -->
    <div class="relative z-10 w-full max-w-sm mx-4 bg-white rounded-2xl shadow-2xl leave-modal-scale-in overflow-hidden">
      <!-- Header -->
      <div class="px-6 pt-6 pb-4">
        <div class="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
          <lucide-icon [img]="icons.Clock" class="w-6 h-6 text-amber-600"></lucide-icon>
        </div>
        <h2 class="text-lg font-bold text-gray-900 text-center mb-2">
          {{ isTestMode() ? 'Leave Test?' : 'Leave Assignment?' }}
        </h2>
        <p class="text-sm text-gray-500 text-center">
          @if (isTestMode()) {
            Your test progress is saved. You can return to continue testing
            where you left off.
          } @else {
            Your progress is saved automatically. You can return from your
            dashboard and continue where you left off.
          }
        </p>
        @if (expiresAt$ | async) {
          <div class="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
            <p class="text-xs text-amber-700 text-center font-medium">
              The timer will keep running while you're away. Make sure to come back and submit before it expires.
            </p>
          </div>
        }
      </div>

      <!-- Actions -->
      <div class="px-6 pb-6 flex flex-col gap-3">
        <button
          type="button"
          (click)="onStay()"
          class="w-full py-3 px-4 rounded-xl font-semibold text-white text-sm transition-all duration-200 hover:opacity-90 active:scale-[0.98] cursor-pointer"
          [style.background-color]="themeColor"
        >
          Stay & Continue
        </button>
        <button
          type="button"
          (click)="onLeaveToDashboard()"
          class="w-full py-3 px-4 rounded-xl font-semibold text-gray-700 text-sm bg-gray-100 hover:bg-gray-200 transition-all duration-200 active:scale-[0.98] cursor-pointer flex items-center justify-center gap-2"
        >
          <span>{{ isTestMode() ? 'Back to Assignment' : 'Go to Dashboard' }}</span>
          <lucide-icon [img]="icons.ArrowRight" class="w-4 h-4"></lucide-icon>
        </button>
      </div>
    </div>
  </div>
}
